import { HotColumn, HotTable } from "@handsontable/react";
import {
  Backdrop,
  CircularProgress,
  Container,
  Popover,
  Typography,
  Button,
} from "@mui/material";

import { NumericCellType, registerCellType } from "handsontable/cellTypes";
import "handsontable/dist/handsontable.full.css";
import { AutoColumnSize, registerPlugin } from "handsontable/plugins";
import { registerAllModules } from "handsontable/registry";

import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { axios } from "../../commons";
import Page from "../elements/Page";

registerCellType(NumericCellType);
registerAllModules();
registerPlugin(AutoColumnSize);

const positions = [
  "LA",
  "LB",
  "LC",
  "LD",
  "LE",
  "LF",
  "RA",
  "RB",
  "RC",
  "RD",
  "RE",
  "RF",
];

const PainDiaryRenderer = ({ TD, value, cellProperties }) => {
  const [painDiary, setPainDiary] = useState({ isShown: false });
  return (
    <>
      <div
        className={cellProperties.className}
        style={{
          padding: "0 4px 0 4px",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        onClick={() => {
          if (value) {
            setPainDiary({ isShown: true });
          }
        }}
      >
        {value}
      </div>
      {!!value && (
        <Popover
          id={painDiary.isShown ? "simple-popover" : undefined}
          open={painDiary.isShown}
          anchorEl={TD}
          onClose={() => {
            setPainDiary({ isShown: false });
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Typography sx={{ p: 2 }}>{value}</Typography>
        </Popover>
      )}
    </>
  );
};

const PatientDetail = () => {
  const { userId } = useParams();

  const [user, setUser] = useState({});
  const [daily, setDaily] = useState([]);
  const [pains, setPains] = useState([]);
  const [surveys, setSurveys] = useState({
    colHeaders: [],
    rowHeaders: [],
    columns: [],
    items: [],
  });
  const [isShownBackdrop, toggleShownBackdrop] = useState(false);

  const load = useCallback(async () => {
    toggleShownBackdrop(true);
    try {
      const results = await Promise.all([
        axios.get(`/admin/users/${userId}/dailyAnswers`),
        axios.get(`/admin/surveys/${userId}`),
      ]);

      const data = results[0].data;
      const survey = results[1].data;
      console.log({ data, survey });

      setUser(data.user);

      const arrDaily = [];
      data.weeks.forEach((week) => {
        week.days.forEach((day) => {
          const row = { ...day, date: day.datetime.substring(0, 10) };

          week.painDiaries.some((diary) => {
            if (diary.date === row.date) {
              row.painDiary = diary.painDiary;
              return true;
            }
            return false;
          });

          arrDaily.push(row);
        });
      });
      setDaily((prev) => [...prev, ...arrDaily]);

      const arrPains = [];
      data.weeks.forEach((week) => {
        week.pains.forEach((pain) => {
          const row = {
            ...pain,
            datetime: DateTime.fromISO(pain.createdAt).toLocaleString(
              DateTime.DATETIME_SHORT
            ),
            positions: pain.painPositions.join(", "),
          };

          positions.forEach((p) => {
            if (pain.painPositions.includes(p)) {
              row[`position${p}`] = "O";
            }
          });

          arrPains.push(row);
        });
      });
      setPains((prev) => [...prev, ...arrPains]);

      Object.entries(survey.sheet[0]).forEach(([k, v]) => {
        survey.sheet[0][k] = DateTime.fromISO(v).toLocaleString(
          DateTime.DATE_SHORT
        );
      });

      setSurveys({
        colHeaders: survey.answers.map(
          (answer) => `${answer.topic} (${answer.createdAt.substring(0, 10)})`
        ),
        rowHeaders: survey.sheet.map((_, idx) => {
          if (idx > 0) {
            const itemIdx = Math.ceil(idx / 2);
            return `${itemIdx}번 ${idx % 2 ? "문제" : "응답"}`;
          } else {
            return "질문 버전";
          }
        }),
        columns: survey.answers.map((answer) => ({ data: answer.id })),
        items: survey.sheet,
      });
    } catch (err) {
      console.error(err);
    }
    toggleShownBackdrop(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Page
        title="Patients"
        //  display="flex" height="100%"
      >
        <Container
          maxWidth="xl"
          // sx={{ display: "flex", flexDirection: "column" }}
          sx={{
            "& .handsontable th, .handsontable td": {
              position: "relative",
            },
            "& .bg-grey": { bgcolor: "#eee" },
          }}
        >
          <Typography variant="h4" sx={{ mb: 5 }}>
            {user.name && (
              <>
                환자 <strong>{user.name}</strong> 님의 기록
              </>
            )}
          </Typography>

          <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>
            일일
          </Typography>
          <HotTable
            data={daily}
            colHeaders={colHeadersForDaily}
            // columns={columnsForDaily}
            readOnly
            // width="100%"
            // height="300"
            settings={{
              height: "auto",
              autoColumnSize: { samplingRatio: 100 },
              licenseKey: "non-commercial-and-evaluation",
              fixedColumnsLeft: 1,
              cells: (row, col) => {
                const cellMeta = {};

                if (row % 14 >= 7) {
                  cellMeta.className = "bg-grey";
                }

                return cellMeta;
              },
            }}
          >
            {columnsForDaily.map((column, idx) => (
              <HotColumn key={idx} settings={column}>
                {idx === 24 ? <PainDiaryRenderer hot-renderer /> : null}
              </HotColumn>
            ))}
          </HotTable>

          <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>
            통증
          </Typography>
          <HotTable
            data={pains}
            colHeaders={colHeadersForPains}
            columns={columnsForPains}
            readOnly
            // width="100%"
            // height="300"
            settings={{
              height: "auto",
              autoColumnSize: { samplingRatio: 100 },
              licenseKey: "non-commercial-and-evaluation",
              fixedColumnsLeft: 1,
            }}
          />

          {surveys.items.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>
                설문
              </Typography>
              <HotTable
                data={surveys.items}
                colHeaders={surveys.colHeaders}
                rowHeaders={surveys.rowHeaders}
                columns={surveys.columns}
                readOnly
                // width="100%"
                // height="300"
                settings={{
                  height: "auto",
                  rowHeaderWidth: 80,
                  autoColumnSize: { samplingRatio: 100 },
                  licenseKey: "non-commercial-and-evaluation",
                }}
              />
            </>
          )}
        </Container>
      </Page>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={isShownBackdrop}
        onClick={() => {
          // toggleShownBackdrop(false);
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default PatientDetail;

const colHeadersForDaily = [
  "날짜",
  "운동 횟수",
  "카페인 잔 수",
  "알콜 잔 수",
  "취침시간",
  "기상시간",
  "수면시간(분)",
  // "어젯밤 잠은 푹 주무셨나요?",
  // "피곤해서 일어나기 힘들었나요?",
  // "일어났을 때 턱이 아프셨나요?",
  // "자는 동안 이갈이를 했거나 이를 악물었나요?",
  "아침질문1",
  "아침질문2",
  "아침질문3",
  "아침질문4",
  "오늘통증점수",
  "개구량(mm)",
  "저녁질문1",
  "저녁질문2",
  "저녁질문3",
  "저녁질문4",
  "저녁질문5",
  "저녁질문6",
  "저녁질문7",
  "저녁질문8",
  "주의사항 확인 횟수",
  "통증 기록 횟수",
  "통증 강도 합계",
  "통증 일기",
];

const columnsForDaily = [
  { data: "date" },
  { data: "numExercises", type: "numeric" },
  { data: "numCaffeine", type: "numeric" },
  { data: "numAlcohol", type: "numeric" },
  { data: "sleep" },
  { data: "rising" },
  { data: "sleepMinutes", type: "numeric" },
  { data: "morningQuestion1" },
  { data: "morningQuestion2" },
  { data: "morningQuestion3" },
  { data: "morningQuestion4" },
  { data: "painLevel", type: "numeric" },
  { data: "openingAmountMillis", type: "numeric" },
  { data: "eveningQuestion1" },
  { data: "eveningQuestion2" },
  { data: "eveningQuestion3" },
  { data: "eveningQuestion4" },
  { data: "eveningQuestion5" },
  { data: "eveningQuestion6" },
  { data: "eveningQuestion7" },
  { data: "eveningQuestion8" },
  { data: "numViewedPrecautions", type: "numeric" },
  { data: "numPainLevel", type: "numeric" },
  { data: "totalPainLevel", type: "numeric" },
  { data: "painDiary", width: 100 },
];

const colHeadersForPains = positions.reduce(
  (acc, cur) => {
    acc.push(`통증 위치: ${cur}`);
    return acc;
  },
  ["기록 시각", "통증 위치", "통증 강도", "통증 상황", "통증 기타 상황"]
);

const columnsForPains = positions.reduce(
  (acc, cur) => {
    acc.push({ data: `position${cur}` });
    return acc;
  },
  [
    { data: "datetime" },
    { data: "positions" },
    { data: "painLevel", type: "numeric" },
    { data: "action" },
    { data: "actionDetail" },
  ]
);
